import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { FileText, Calendar, ArrowRight, Tag } from 'lucide-react';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/blogs`)
      .then(res => {
        setBlogs(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <FileText className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Insights & Resources</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="blog-title">
            Blog
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Educational insights, study tips, and resources for STEM students
          </p>
        </motion.div>

        {blogs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-12 text-center"
          >
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-semibold text-white mb-2">No Posts Yet</h3>
            <p className="text-slate-400">Check back soon for educational articles and resources.</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog, idx) => (
              <motion.article
                key={blog.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card overflow-hidden card-hover group"
              >
                {blog.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-slate-400 text-sm mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(blog.created_at).toLocaleDateString('en-SG', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-white mb-2 line-clamp-2 group-hover:text-electric-cyan transition-colors">
                    {blog.title}
                  </h3>
                  <p className="text-slate-400 mb-4 line-clamp-3">{blog.excerpt}</p>
                  
                  {blog.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {blog.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs bg-surface-navy px-2 py-1 rounded text-slate-400 flex items-center gap-1">
                          <Tag className="w-3 h-3" /> {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <Link 
                    to={`/blog/${blog.slug}`} 
                    className="text-electric-cyan text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                    data-testid={`blog-read-${blog.slug}`}
                  >
                    Read More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
